import { Injectable, Inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { EMPTY, Observable } from 'rxjs';
import { NgbDateStruct, NgbDate } from '@ng-bootstrap/ng-bootstrap';

import { API_URL } from '@common/app/app.config';

import {
    Registration,
    PanelsProducers,
    FlowType,
    YesNoDontKnow,
} from '@spjp/model/registration';
import { SolarInstallation } from '@spjp/model/solar-installation';
import { SolarSimulationResponse } from '@spjp/model/solar-simulation';
import { catchError } from 'rxjs/operators';
import { SurveyPeriods } from './survey-periods';
import { SpjpCommunity } from '@spjp/model/community';

@Injectable()
export class SolarService {
    url: string;
    // https://ichoosr.atlassian.net/browse/SPJP-996 All community to be communityWithEVPHV in 2022
    // readonly communityWithEVPHV = ['osaka', 'gunma'];

    constructor(
        protected http: HttpClient,
        @Inject(API_URL) protected baseUrl: string,
    ) {
        this.url = this.baseUrl;
    }

    getSimulationForRegistration(
        registration: Registration,
    ): Observable<SolarSimulationResponse> {
        if (!registration) {
            return EMPTY;
        }

        const community = registration.community as SpjpCommunity;

        const payload = new Registration();
        Object.assign(payload, registration);

        if (community!.isB2B === true) {
            if (
                payload.product.hasEstimatedCapacity &&
                !!payload.product.estimatedCapacity
            ) {
                return EMPTY;
            }

            if (
                !payload.product.roofDimensionA ||
                !payload.product.roofDimensionB
            ) {
                return EMPTY;
            }
        }

        return this.http
            .post<SolarSimulationResponse>(this.url + 'estimate', payload)
            .pipe(
                catchError((_) => {
                    return EMPTY;
                }),
            );
    }

    getProposal(registrationId: string): Observable<SolarInstallation> {
        const query = 'registrationId=' + registrationId;

        return this.http.get<SolarInstallation>(
            this.url + 'solar/proposal?' + query,
        );
    }

    getMaxSurveyDate(registration: Registration): NgbDateStruct {
        const today = new Date();
        let result = new Date();

        if (registration?.community?.code && registration?.auction) {
            result =
                SurveyPeriods.getMaxDateBase(
                    registration.community.code,
                    registration.proposal?.supplier?.code,
                ) ?? result;
        }

        // When result date is in the past or today - fallback
        if (result <= today) {
            // Default is today + 3 months
            result = today;
            result.setMonth(today.getMonth() + 3);
        }

        return {
            year: result.getFullYear(),
            month: result.getMonth() + 1,
            day: result.getDate(),
        };
    }

    getMinSurveyDate(registration: Registration): NgbDateStruct {
        // default is today + 4 days
        let daySpan = 4;

        if (registration?.community?.code && registration?.auction) {
            daySpan =
                SurveyPeriods.getMinDaySpan(
                    registration.community.code,
                    registration.proposal?.supplier?.code,
                ) ?? daySpan;
        }

        const d = new Date();
        d.setDate(d.getDate() + daySpan);
        return {
            year: d.getFullYear(),
            month: d.getMonth() + 1,
            day: d.getDate(),
        };
    }

    // This method has to return lambda (date: NgbDate, current: {month: number}) => {} which returns boolean
    isSurveyDateDisabledFunc(
        registration: Registration,
    ): (date: NgbDate, current: { month: number }) => boolean {
        if (
            registration &&
            registration?.community?.code &&
            registration?.auction?.code
        ) {
            if (registration?.community?.code === 'kyoto') {
                // exclude Aug 13th - 16th
                return (date: NgbDate, current: { month: number }) =>
                    date.month === 8 && [13, 14, 15, 16].includes(date.day);
            }
        }
        return (date: NgbDate, current: { month: number }) => false;
    }

    isFlatRoofInstallationAllowed(_communityCode: string): boolean {
        return true;
    }

    showMShapedRoof(communityCode: string): boolean {
        if (communityCode === 'hokkaido') {
            return true;
        }
        return false;
    }

    showRestrictedAreaMap(communityCode: string): boolean {
        if (communityCode === 'kyoto') {
            return true;
        }
        return false;
    }

    showFlatRoofWarning(communityCode: string): boolean {
        if (communityCode === 'kyoto') {
            return true;
        }
        return false;
    }

    evOrPHVEnabled(communityCode: string): boolean {
        // https://ichoosr.atlassian.net/browse/SPJP-996 All community to be communityWithEVPHV in 2022
        return true;
        // return this.communityWithEVPHV.includes(communityCode);
    }

    wantsBatteryOnlyWithoutPanels(registration: Registration): boolean {
        if (
            registration?.community?.code === 'kanagawa' &&
            registration?.product?.hasPanelsAlready &&
            registration?.product?.panelsProducer === PanelsProducers.Other &&
            registration?.product?.panelsCapacity === 0
        ) {
            return true;
        }
        return false;
    }

    getMinimalPanelsCapacity(communityCode: string): number {
        if (communityCode === 'kanagawa') {
            return 0;
        }
        return 0.1;
    }

    getMaximumPanelsCapacity(): number {
        return 20;
    }

    getOfferType(registration: Registration): string {
        if (registration.product.hasPanelsAlready) {
            return 'bt_only';
        }
        if (
            !registration.product.hasPanelsAlready &&
            !registration.product.wantsToUseBattery
        ) {
            return 'sp_only';
        }
        if (
            !registration.product.hasPanelsAlready &&
            registration.product.wantsToUseBattery
        ) {
            return 'both_sp_bt';
        }
        return '';
    }

    getOfferTypeForNewFlow(registration: Registration): string {
        if (registration.flowType === FlowType.Battery) {
            return 'bt_only';
        } else if (
            (registration.flowType === FlowType.SolarPanels &&
                !registration?.product?.wantsToUseBattery) ||
            registration?.product?.wantsToUseBattery === YesNoDontKnow.No
        ) {
            return 'sp_only';
        }
        if (
            (registration.flowType === FlowType.SolarPanelsAndBattery &&
                !registration?.product?.wantsToUseBattery) ||
            registration?.product?.wantsToUseBattery === YesNoDontKnow.Yes
        ) {
            return 'both_sp_bt';
        }
        if (registration.flowType === FlowType.BatteryWithoutSolarPanels) {
            return 'bt_wo_sp';
        }
        return '';
    }
}
